import { extractFaqEntries } from 'helpers/parsing/extractFaqEntries';
import { extractYoutubeVideoId } from 'helpers/parsing/extractYoutubeVideoId';
import Head from 'next/head';
import { BlogArticleDetailType } from 'types/blogArticle';

type BlogArticleMetadataProps = {
    article: BlogArticleDetailType;
};

export const BlogArticleMetadata: FC<BlogArticleMetadataProps> = ({ article }) => {
    const metadataType = article.isRecipe ? 'Recipe' : 'NewsArticle';
    const description =
        article.isRecipe && article.perex
            ? article.perex
                  .replace(/<\/(p|h[1-6]|br)>/gi, '\n')
                  .replace(/<[^>]+>/g, '')
                  .replace(/&nbsp;/g, ' ')
                  .trim()
            : undefined;

    const youtubeVideoId = extractYoutubeVideoId(article.text);
    const videoObject = youtubeVideoId
        ? {
              '@context': 'https://schema.org/',
              '@type': 'VideoObject',
              name: article.name,
              thumbnailUrl: `https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`,
              embedUrl: `https://www.youtube.com/embed/${youtubeVideoId}`,
              ...(description ? { description } : {}),
          }
        : null;

    const jsonLdItems: object[] = [
        {
            '@context': 'https://schema.org/',
            '@type': metadataType,
            headline: article.name,
            image: article.blogArticlesGridImages,
            datePublished: article.publishDate,
            ...(description ? { description } : {}),
        },
    ];

    if (videoObject) {
        jsonLdItems.push(videoObject);
    }

    const faqEntries = article.text === null ? [] : extractFaqEntries(article.text);
    if (faqEntries.length > 0) {
        jsonLdItems.push({
            '@context': 'https://schema.org/',
            '@type': 'FAQPage',
            mainEntity: faqEntries,
        });
    }

    return (
        <Head>
            <script
                key="article-metadata"
                id="blog-article-metadata"
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(jsonLdItems),
                }}
            />
        </Head>
    );
};
